<template>
    <div class="card h-100 border-0 shadow-sm invoice-payment-link-card">
        <div class="card-header bg-white border-0 pt-3 pb-0 px-4">
            <h3 class="card-title text-uppercase font-weight-500 mb-0 d-flex align-items-center">
                <Icon icon-class="ui-credit-card-refresh" size="4" class="text-primary mr-2" />
                {{ t('invoices-invoice_payment_link') }}
            </h3>
        </div>

        <div class="card-body p-3 px-4 d-flex flex-column">
            <div v-if="invoice && invoice.payment_link_url" class="active-link-container flex-grow-1 d-flex flex-column justify-content-center">
                <div class="active-link-card p-3 rounded-xl border">
                    <div class="d-flex align-items-center mb-2">
                        <div class="card-icon-wrapper mr-3">
                            <Icon icon-class="ui-credit-card-refresh" size="4" class="text-primary" />
                        </div>
                        <div class="flex-grow-1">
                            <h4 class="mb-0 font-weight-500 text-dark title-link">{{ t('invoices-payment_link_active_title') }}</h4>
                            <p class="text-muted smallest mb-0 mt-1">{{ t('invoices-payment_link_active_hint') }}</p>
                        </div>
                    </div>

                    <div v-if="expiresAtFormatted" class="text-muted smallest mb-3 mt-2">
                        <Icon icon-class="ui-clock" size="2" class="mr-1" />
                        {{ t('invoices-payment_link_expires_at') }}:
                        <span class="text-dark font-weight-600">{{ expiresAtFormatted }}</span>
                    </div>

                    <div class="d-flex flex-wrap gap-2 align-items-center mt-3">
                        <kv-button
                            variant="outlined"
                            prepend-icon="ui-link-01"
                            size="small"
                            @click="openCheckout"
                        >
                            {{ t('invoices-payment_link_view') }}
                        </kv-button>

                        <kv-copy-button
                            variant="outlined"
                            size="small"
                            :value="invoice.payment_link_url"
                            :text="t('invoices-payment_link_copy')"
                            :success-text="t('invoices-payment_link_copied')"
                        />

                        <kv-button
                            variant="text"
                            class="text-danger ml-auto"
                            prepend-icon="ui-trash-01"
                            size="small"
                            @click="openRemoveModal"
                        >
                            {{ t('invoices-payment_link_remove') }}
                        </kv-button>
                    </div>
                </div>
            </div>

            <div v-else class="generate-link-container flex-grow-1 d-flex flex-column justify-content-center">
                <div class="generate-link-box p-4 text-center border-dashed rounded-xl flex-grow-1 d-flex flex-column align-items-center justify-content-center">
                    <div class="link-icon-wrapper mb-3">
                        <Icon icon-class="ui-link-01" size="6" class="text-muted" />
                    </div>
                    <h4 class="mb-1 font-weight-500 text-dark">{{ t('invoices-generate_payment_link') }}</h4>
                    <p class="text-muted small mb-3 max-w-360">{{ t('invoices-payment_link_hint') }}</p>

                    <kv-button
                        variant="filled"
                        prepend-icon="ui-credit-card-refresh"
                        :loading="generating"
                        :disabled="!canGenerate"
                        @click="generatePaymentLink"
                    >
                        {{ t('invoices-generate_payment_link') }}
                    </kv-button>

                    <p v-if="!canGenerate && generateDisabledReason" class="text-muted smallest mt-2 mb-0">
                        {{ generateDisabledReason }}
                    </p>
                </div>
            </div>

            <p v-if="error" class="text-danger small mt-3 mb-0 text-center animate-shake">
                <Icon icon-class="ui-alert-circle" class="mr-1" /> {{ error }}
            </p>
        </div>

        <KvModal
            ref="removeLinkModal"
            v-if="showConfirmModal"
            id="remove-payment-link-modal"
            @close="showConfirmModal = false"
        >
            <template #title>
                {{ t('invoices-payment_link_remove_confirm_title') }}
            </template>
            <template #body>
                <div class="d-flex align-items-start">
                    <div class="modal-alert-icon text-warning mr-3 mt-1">
                        <Icon icon-class="ui-alert-triangle" size="5" />
                    </div>
                    <div>
                        <p class="text-dark font-weight-500 mb-1">
                            {{ t('invoices-payment_link_remove_confirm_title') }}
                        </p>
                        <p class="text-muted small mb-0">
                            {{ t('invoices-payment_link_remove_confirm_body') }}
                        </p>
                    </div>
                </div>
            </template>
            <template #footer>
                <kv-button
                    variant="outlined"
                    @click="showConfirmModal = false"
                    :disabled="removing"
                >
                    {{ t('invoices-cancel') }}
                </kv-button>
                <kv-button
                    variant="danger"
                    :loading="removing"
                    @click="handleConfirmRemove"
                >
                    {{ t('invoices-yes_cancel_and_remove') }}
                </kv-button>
            </template>
        </KvModal>
    </div>
</template>

<script>
import keviApi from '../../keviApi';
import KvModal from "../../components/modal/modal.vue";
import { extractApiError } from "../helpers";

export default {
    name: "invoice-payment-link",
    components: {
        KvModal
    },
    props: {
        invoice: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            generating: false,
            removing: false,
            showConfirmModal: false,
            error: null,
            lastResult: null
        };
    },
    computed: {
        canGenerate() {
            if (!this.invoice) return false;
            const isPendingOrDraft = ['pending', 'draft'].includes(this.invoice.status);
            const hasPositiveAmount = (parseFloat(this.invoice.total) || 0) > 0;
            return isPendingOrDraft && hasPositiveAmount;
        },
        generateDisabledReason() {
            if (!this.invoice) return null;
            if (['paid', 'cancelled'].includes(this.invoice.status)) {
                return 'Faturas pagas ou canceladas não podem receber link de pagamento.';
            }
            if ((parseFloat(this.invoice.total) || 0) <= 0) {
                return 'O valor total da fatura deve ser maior que zero.';
            }
            return null;
        },
        expiresAtFormatted() {
            const expires = this.lastResult?.expires_at;
            if (!expires) return null;
            try {
                return new Date(expires).toLocaleString();
            } catch {
                return null;
            }
        }
    },
    methods: {
        async generatePaymentLink() {
            if (!this.canGenerate) return;

            this.error = null;
            this.generating = true;

            try {
                const response = await this.$http.post(
                    `${keviApi.endpoint}invoices/${this.invoice.id}/payment-link`,
                    {},
                    { headers: keviApi.getAuthHeader() }
                );

                if (response.status === 200 && response.body.payment_url) {
                    this.$store.dispatch('notification/success', this.t('invoices-payment_link_generation_success'));
                    this.lastResult = response.body;
                    this.$emit('updated', response.body.payment_url);
                    this.$emit('refresh');
                } else {
                    this.error = extractApiError(response, this.t('invoices-payment_link_generation_error'));
                }
            } catch (e) {
                this.error = extractApiError(e, this.t('invoices-payment_link_generation_error'));
            } finally {
                this.generating = false;
            }
        },
        openRemoveModal() {
            this.showConfirmModal = true;
            this.$nextTick(() => {
                if (this.$refs.removeLinkModal) {
                    this.$refs.removeLinkModal.open();
                }
            });
        },
        async handleConfirmRemove() {
            this.removing = true;
            this.error = null;

            try {
                const response = await this.$http.delete(
                    `${keviApi.endpoint}invoices/${this.invoice.id}/payment-link`,
                    { headers: keviApi.getAuthHeader() }
                );

                if (response.status === 200) {
                    this.$store.dispatch('notification/success', this.t('invoices-payment_link_remove_success'));
                    this.$emit('updated', null);
                    this.showConfirmModal = false;
                    if (this.$refs.removeLinkModal) {
                        this.$refs.removeLinkModal.close();
                    }
                    this.$emit('refresh');
                } else {
                    this.error = extractApiError(response, this.t('invoices-payment_link_remove_error'));
                }
            } catch (e) {
                this.error = extractApiError(e, this.t('invoices-payment_link_remove_error'));
            } finally {
                this.removing = false;
            }
        },
        openCheckout() {
            if (this.invoice && this.invoice.payment_link_url) {
                window.open(this.invoice.payment_link_url, '_blank');
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.invoice-payment-link-card {
    border-radius: 20px;
    border: 1px solid #E5E7EB !important;
    background: #FFFFFF;
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
        box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.05) !important;
    }

    .card-title {
        font-size: 13px;
        color: #6B7280;
        letter-spacing: 0.1em;
    }

    .border-dashed {
        border: 2px dashed #E5E7EB;
        background-color: #F9FAFB;
    }

    .rounded-xl {
        border-radius: 12px;
    }

    .smallest {
        font-size: 0.75rem;
    }

    .max-w-360 {
        max-width: 360px;
    }

    .active-link-card {
        background: #F9FAFB;
        border: 1px solid #E5E7EB !important;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);

        .card-icon-wrapper {
            width: 44px;
            height: 44px;
            border-radius: 12px;
            background: #FFFFFF;
            border: 1px solid #E5E7EB;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
        }

        .title-link {
            font-size: 1rem;
        }
    }

    .generate-link-box {
        .link-icon-wrapper {
            width: 56px;
            height: 56px;
            border-radius: 50%;
            background-color: #F3F4F6;
            display: flex;
            align-items: center;
            justify-content: center;
        }
    }

    .gap-2 {
        gap: 8px;
    }

    .animate-shake {
        animation: shake 0.4s ease-in-out;
    }

    @keyframes shake {
        0%, 100% { transform: translateX(0); }
        25% { transform: translateX(-4px); }
        75% { transform: translateX(4px); }
    }
}
</style>
